import React, {
  Suspense,
  lazy,
} from 'react'

import {
  createBrowserRouter,
  Navigate,
} from 'react-router-dom'

import Layout from '@/pages/Layout'
import Login from '@/pages/Login'

import {
  AuthRoute,
} from '@/components/AuthRoute'

import {
  ProductRoute,
} from '@/components/ProductRoute'

import RoleRoute from '@/components/RoleRoute'


// ======================================================
// Lazy Pages
// ======================================================

const Home =
  lazy(() =>
    import('@/pages/Home')
  )


const Profile =
  lazy(() =>
    import('@/pages/Profile')
  )


// ======================================================
// Administration
// ======================================================

const LotteryDrawManagement =
  lazy(() =>
    import('@/pages/LotteryDrawManagement')
  )


// ======================================================
// Lotto 6/49
// ======================================================

const Lotto649 =
  lazy(() =>
    import('@/pages/Lotto649')
  )


const Lotto649MyTickets =
  lazy(() =>
    import('@/pages/Lotto649/MyTickets')
  )


const Lotto649ScanTicket =
  lazy(() =>
    import('@/pages/Lotto649/ScanTicket')
  )


// ======================================================
// Lotto MAX
// ======================================================

const LottoMax =
  lazy(() =>
    import('@/pages/LottoMax')
  )


// ======================================================
// Gaming
// ======================================================

// 1. 付费轮盘分析组件

const Roulette =
  lazy(() =>
    import('@/pages/Roulette')
  )


// 2. 免费轮盘游戏组件

const RouletteGame =
  lazy(() =>
    import('@/pages/RouletteGame')
  )


// ======================================================
// Loading
// ======================================================

const PageLoading = () => (

  <div
    style={{
      minHeight: '240px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#64748b',
      fontSize: '13px',
    }}
  >

    Loading...

  </div>

)


// ======================================================
// Lazy Wrapper
// ======================================================

const LazyPage = ({
  children,
}) => (

  <Suspense
    fallback={
      <PageLoading />
    }
  >

    {children}

  </Suspense>

)


// ======================================================
// Product Routes
//
// 收费付费模块
//
// All protected by ProductRoute.
// ======================================================

const productRoutes = [

  // ==================================================
  // Lotto 6/49
  //
  // /lotto-649
  // ==================================================

  {
    path: 'lotto-649',

    productKey: '649',

    element:
      <Lotto649 />,
  },


  // ==================================================
  // Lotto 6/49
  //
  // My Tickets
  //
  // /lotto-649/my-tickets
  // ==================================================

  {
    path: 'lotto-649/my-tickets',

    productKey: '649',

    element:
      <Lotto649MyTickets />,
  },


  // ==================================================
  // Lotto 6/49
  //
  // Scan Ticket
  //
  // /lotto-649/scan-ticket
  // ==================================================

  {
    path: 'lotto-649/scan-ticket',

    productKey: '649',

    element:
      <Lotto649ScanTicket />,
  },


  // ==================================================
  // Lotto MAX
  //
  // /lotto-max
  // ==================================================

  {
    path: 'lotto-max',

    productKey: 'max',

    element:
      <LottoMax />,
  },


  // ==================================================
  // Roulette
  //
  // /roulette
  // ==================================================

  {
    path: 'roulette',

    productKey: 'roulette',

    element:
      <Roulette />,
  },

]


// ======================================================
// User Routes
//
// 免费模块 / 基础功能
//
// Available to every authenticated user.
// ======================================================

const userRoutes = [

  // ==================================================
  // Profile
  // ==================================================

  {
    path: 'profile',

    element:
      <Profile />,
  },


  // ==================================================
  // Free Roulette Game
  //
  // No ProductRoute required.
  // ==================================================

  {
    path: 'roulette-game',

    element:
      <RouletteGame />,
  },

]


// ======================================================
// Build Product Route
// ======================================================

const buildProductRoute = ({
  path,
  productKey,
  element,
}) => ({

  path,

  element: (

    <ProductRoute
      productKey={
        productKey
      }
    >

      <LazyPage>

        {element}

      </LazyPage>

    </ProductRoute>

  ),

})


// ======================================================
// Build User Route
// ======================================================

const buildUserRoute = ({
  path,
  element,
}) => ({

  path,

  element: (

    <LazyPage>

      {element}

    </LazyPage>

  ),

})


// ======================================================
// Build Admin Route
//
// Administrator / OM only
// ======================================================

const buildAdminRoute = ({
  path,
  element,
}) => ({

  path,

  element: (

    <RoleRoute
      roles={[
        'Administrator',
        'OM',
      ]}
    >

      <LazyPage>

        {element}

      </LazyPage>

    </RoleRoute>

  ),

})


// ======================================================
// Router
// ======================================================

const router =
  createBrowserRouter([

    // ==================================================
    // Login
    // ==================================================

    {
      path: '/login',

      element:
        <Login />,
    },


    // ==================================================
    // Authenticated Application
    // ==================================================

    {

      path: '/',

      element: (

        <AuthRoute>

          <Layout />

        </AuthRoute>

      ),


      children: [

        // ==============================================
        // Home
        // ==============================================

        {
          index: true,

          element: (

            <LazyPage>

              <Home />

            </LazyPage>

          ),

        },


        // ==============================================
        // Account & Free Tools
        // ==============================================

        ...userRoutes.map(
          buildUserRoute
        ),


        // ==============================================
        // Products
        //
        // /lotto-649
        // /lotto-649/my-tickets
        // /lotto-649/scan-ticket
        // /lotto-max
        // /roulette
        // ==============================================

        ...productRoutes.map(
          buildProductRoute
        ),


        // ==============================================
        // Administration
        //
        // Administrator / OM only
        //
        // /lottery-draws
        // ==============================================

        buildAdminRoute({

          path:
            'lottery-draws',

          element:
            <LotteryDrawManagement />,

        }),

      ],

    },


    // ==================================================
    // Fallback
    // ==================================================

    {

      path: '*',

      element: (

        <Navigate
          to="/"
          replace
        />

      ),

    },

  ])


export default router

